/**
 * AI俳優の「キャラクター」対応 (ethnicity='character') 判定・表示ラベルの共通ヘルパー。
 *
 * ethnicity の正準値は 'jp' / 'foreigner' / 'character' / null (BE の
 * actor_admin_service.actor_is_character と対をなす)。'character' の直書き判定を
 * 各画面 (ウィザード・管理フォーム・俳優一覧・ピッカー) へ散らさないよう、
 * 判定はここに集約する。
 */

/** ethnicity の正準値のうち「キャラクター」を表す値。 */
export const CHARACTER_ETHNICITY = 'character';

/** ethnicity を含みうる最小限の型 (Actor / ExclusiveActor / PartnerActor / WizardData など)。 */
export type EthnicityLike = { ethnicity?: string | null } | null | undefined;

/** 俳優 (または ethnicity を持つ任意の値) がキャラクターか判定する。 */
export function isCharacterActor(actor: EthnicityLike): boolean {
  return !!actor && actor.ethnicity === CHARACTER_ETHNICITY;
}

/** キャスティングUI (人種セグメント) の正準値。'none' は未指定/不明。 */
export type EthnicitySegment = 'jp' | 'foreigner' | 'character' | 'none';

/**
 * 俳優の ethnicity をキャスティングUI用セグメント値へ正規化する共通 helper。
 * 正準値は jp / foreigner / character / null。旧値 'foreign' は 'foreigner' に
 * 正規化する。未知値・null は 'none' (指定なし) を返す。
 */
export function normalizeEthnicitySegment(ethnicity: string | null | undefined): EthnicitySegment {
  if (ethnicity === 'jp') return 'jp';
  if (ethnicity === 'foreigner' || ethnicity === 'foreign') return 'foreigner';
  if (ethnicity === CHARACTER_ETHNICITY) return 'character';
  return 'none';
}

/** ethnicity の表示ラベル (3値: 日本人/外国人/キャラクター)。未指定は null。 */
export function getEthnicityLabel(ethnicity: string | null | undefined): string | null {
  if (!ethnicity) return null;
  if (ethnicity === 'jp') return '日本人';
  if (ethnicity === 'foreigner' || ethnicity === 'foreign') return '外国人';
  if (ethnicity === CHARACTER_ETHNICITY) return 'キャラクター';
  return ethnicity;
}

/** gender の表示ラベル。キャラクターは未指定 (null) がありうるため '未指定' を返す。 */
export function getActorGenderLabel(gender: string | null | undefined): string {
  if (gender === 'male') return '男性';
  if (gender === 'female') return '女性';
  return '未指定';
}

/** face-safe 検査 (character_sheet_face_scan_status) の状態値。null/undefined = 未検査。 */
export type FaceScanStatus = 'pending' | 'safe' | 'restricted' | 'error' | null | undefined;

/**
 * face-safe 検査状態のユーザー向けラベル。キャラクター俳優のみ意味を持つ
 * (人間俳優には表示しない)。safe 以外は Seedance でキャラクターシートを
 * 参照できない (BE が fail-close で拒否する) ため、利用可否を併記する。
 */
export function getFaceScanStatusLabel(status: FaceScanStatus): string {
  switch (status) {
    case 'safe': return '検査済み（Seedance利用可）';
    case 'pending': return '検査中';
    case 'restricted': return '実在の顔を含む可能性あり（Seedance利用不可）';
    case 'error': return '検査エラー（再検査が必要・Seedance利用不可）';
    default: return '未検査（Seedance利用不可）';
  }
}

/**
 * 俳優一覧・ピッカーの属性サマリ表記。
 * キャラクターは「キャラクター」(gender があれば「キャラクター・女性」等)、
 * 人間は性別のみ ("男性" 等、従来どおり)。
 */
export function getActorAttributeSummary(actor: {
  ethnicity?: string | null;
  gender?: string | null;
}): string {
  if (isCharacterActor(actor)) {
    const genderLabel = actor.gender === 'male' || actor.gender === 'female'
      ? getActorGenderLabel(actor.gender)
      : null;
    return genderLabel ? `キャラクター・${genderLabel}` : 'キャラクター';
  }
  return getActorGenderLabel(actor.gender);
}
